'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { AnimatePresence, motion } from 'framer-motion'
import { X, Search, Loader2 } from 'lucide-react'

// The key must come from the environment (NEXT_PUBLIC_GIPHY_API_KEY). No
// hardcoded fallback — a committed key is a leaked key.
const GIPHY_KEY = process.env.NEXT_PUBLIC_GIPHY_API_KEY ?? ''

interface Gif {
  id: string
  preview: string // small still/animated preview for the grid
  url: string // url to send
}

function mapGifs(data: any[]): Gif[] {
  return (data ?? [])
    .map((g) => ({
      id: g.id,
      preview: g.images?.fixed_width?.url ?? g.images?.downsized?.url,
      url: g.images?.downsized_medium?.url ?? g.images?.original?.url,
    }))
    .filter((g) => g.preview && g.url)
}

export function GifPicker({
  open,
  onClose,
  onSelect,
}: {
  open: boolean
  onClose: () => void
  onSelect: (url: string) => void
}) {
  const [mounted, setMounted] = useState(false)
  const [query, setQuery] = useState('')
  const [gifs, setGifs] = useState<Gif[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const reqId = useRef(0)

  useEffect(() => setMounted(true), [])

  const fetchGifs = useCallback(async (q: string) => {
    const id = ++reqId.current
    setLoading(true)
    setError(null)
    const endpoint = q.trim()
      ? `https://api.giphy.com/v1/gifs/search?api_key=${GIPHY_KEY}&q=${encodeURIComponent(q)}&limit=24&rating=pg-13`
      : `https://api.giphy.com/v1/gifs/trending?api_key=${GIPHY_KEY}&limit=24&rating=pg-13`
    try {
      const res = await fetch(endpoint)
      if (!res.ok) {
        if (id === reqId.current) {
          setGifs([])
          setError(
            res.status === 401 || res.status === 403
              ? 'GIF search is unavailable — a valid Giphy API key is required (set NEXT_PUBLIC_GIPHY_API_KEY).'
              : 'Could not load GIFs. Please try again.'
          )
        }
        return
      }
      const json = await res.json()
      if (id === reqId.current) setGifs(mapGifs(json.data))
    } catch {
      if (id === reqId.current) {
        setGifs([])
        setError('Could not load GIFs. Check your connection and try again.')
      }
    } finally {
      if (id === reqId.current) setLoading(false)
    }
  }, [])

  // Load trending on open; debounce searches.
  useEffect(() => {
    if (!open) return
    const t = setTimeout(() => fetchGifs(query), query ? 350 : 0)
    return () => clearTimeout(t)
  }, [open, query, fetchGifs])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!mounted) return null

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[110] flex items-start justify-center bg-black/80 p-4 pt-20 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div
            onClick={(e) => e.stopPropagation()}
            initial={{ opacity: 0, y: 16, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.98 }}
            transition={{ type: 'spring', stiffness: 300, damping: 26 }}
            className="flex max-h-[70vh] w-full max-w-lg flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-2xl"
          >
            {/* Header */}
            <div className="flex items-center justify-between border-b border-border px-4 py-3">
              <button
                onClick={onClose}
                className="text-muted-foreground transition-colors hover:text-foreground"
                aria-label="Close"
              >
                <X className="size-5" />
              </button>
              <h2 className="text-base font-semibold">Choose a GIF</h2>
              <span className="w-5" />
            </div>

            {/* Search */}
            <div className="p-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                <input
                  autoFocus
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search for GIFs"
                  className="h-10 w-full rounded-lg border border-border bg-background pl-10 pr-3 text-sm outline-none focus:border-primary"
                />
              </div>
            </div>

            {/* Grid */}
            <div className="min-h-0 flex-1 overflow-y-auto px-3 pb-3">
              {loading && gifs.length === 0 ? (
                <div className="flex justify-center py-10 text-muted-foreground">
                  <Loader2 className="size-5 animate-spin" />
                </div>
              ) : error ? (
                <p className="px-4 py-10 text-center text-sm text-muted-foreground">
                  {error}
                </p>
              ) : gifs.length === 0 ? (
                <p className="py-10 text-center text-sm text-muted-foreground">
                  No GIFs found.
                </p>
              ) : (
                <div className="columns-2 gap-2 sm:columns-3">
                  {gifs.map((g) => (
                    <button
                      key={g.id}
                      onClick={() => {
                        onSelect(g.url)
                        onClose()
                      }}
                      className="group mb-2 block w-full overflow-hidden rounded-lg ring-1 ring-white/10 transition-transform hover:scale-[1.02]"
                    >
                      {/* eslint-disable-next-line @next/next/no-img-element */}
                      <img src={g.preview} alt="" loading="lazy" className="w-full" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="border-t border-border px-3 py-2 text-center text-[11px] text-muted-foreground">
              Powered by GIPHY
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  )
}
